import React, { useEffect, useMemo, useRef, useState } from 'react';
import { track } from '../utils/analytics';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import GitHubIcon from '@mui/icons-material/GitHub';
import XIcon from '@mui/icons-material/X';
import config from '../config';
import { siteOrigin } from './ui/ChartExportButton';
import { fetchUpcomingMatches } from '../data/iplSchedule';
import ExploreMenu from './nav/ExploreMenu';
import MiniWagonWheel from './MiniWagonWheel';
import { getTeamColor, readableOnDark, textOn } from '../utils/teamColors';
import SearchBar from './search/SearchBar';
import { colors as dark, fonts } from '../theme/hindsightDark';
import { useFormat } from '../context/FormatContext';
import playerProfileUrl from '../utils/playerProfileUrl';

// Mapped onto the shared dark-theme tokens rather than redefining them; see
// src/theme/hindsightDark.js. The names are kept short because they are used densely
// throughout this file. Note `hairline` was rgba(255,255,255,0.06) here and 0.07 in the
// query builder -- the shared token resolves that to 0.07.
const C = {
  bg: dark.bg,
  surface: dark.surface1,
  raised: dark.surface2,
  muted: dark.input,
  inset: dark.inset,
  hairline: dark.border,
  hairlineStrong: dark.borderStrong,
  hi: dark.textHi,
  mid: dark.textMed,
  soft: dark.textLo,
  low: dark.textFaint,
  faint: dark.textGhost,
  lime: dark.accent,
  limeHover: dark.accentHover,
  gold: dark.gold,
  red: dark.red,
};

const DATE_OPTIONS = [
  { value: 'all', label: 'All time', mobileLabel: 'All' },
  { value: '7', label: 'Last 7 days', mobileLabel: '7 days' },
  { value: '30', label: 'Last 30 days', mobileLabel: '30 days' },
  { value: '90', label: 'Last 3 months', mobileLabel: '3 months' },
];

const ELO_RANGE_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: '24m', label: 'Last 24 months' },
  { value: '12m', label: 'Last 12 months' },
  { value: 'ytd', label: '2026 only' },
];


const toDateLabel = (value) => {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const dateInput = (date) => date.toISOString().slice(0, 10);

const routeTeam = (team) => `/team?team=${encodeURIComponent(team || '')}&autoload=true`;
const routePlayer = (player) => `/player?name=${encodeURIComponent(player || '')}&autoload=true`;
const routePreview = (match) => (
  `/venue?venue=${encodeURIComponent(match.venue || '')}` +
  `&team1=${encodeURIComponent(match.team1Abbr || match.team1 || '')}` +
  `&team2=${encodeURIComponent(match.team2Abbr || match.team2 || '')}` +
  `&includeInternational=true&topTeams=20&autoload=true` +
  // The preview is one format's record; ODI fixtures must open in ODI, not the global default.
  `&fmt=${match.format === 'ODI' ? 'mens-odi' : 'mens-t20'}` +
  `${match.matchId ? `&matchId=${encodeURIComponent(match.matchId)}` : ''}`
);

const scrollTrack = (ref, direction) => {
  const node = ref.current;
  if (!node) return;
  node.scrollBy({
    left: direction * Math.min(node.clientWidth * 0.85, 640),
    behavior: 'smooth',
  });
};

const flattenRecentMatches = (data) => {
  if (!data) return [];
  if (Array.isArray(data.matches)) return data.matches;
  return (data.groups || []).flatMap((group) => group.matches || []);
};

const sectionTitleSx = {
  color: C.hi,
  fontFamily: fonts.display,
  fontWeight: 700,
  fontSize: { xs: 20, md: 25 },
  lineHeight: 1.05,
};

const Kicker = ({ children, color = C.lime }) => (
  <Typography
    sx={{
      color,
      fontFamily: fonts.mono,
      fontSize: 11,
      letterSpacing: '0.16em',
      textTransform: 'uppercase',
      fontWeight: 600,
      mb: 0.75,
    }}
  >
    {children}
  </Typography>
);

const SectionHeader = ({ kicker, kickerColor, title, subtitle, action }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, mb: 1.75 }}>
    <Box sx={{ minWidth: 0 }}>
      <Kicker color={kickerColor}>{kicker}</Kicker>
      <Typography sx={sectionTitleSx}>{title}</Typography>
      {subtitle && (
        <Typography sx={{ color: C.soft, mt: 0.6, fontFamily: fonts.body, fontSize: { xs: 13, md: 14 } }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    {action}
  </Box>
);

const CarouselButtons = ({ trackRef, hide }) => {
  if (hide) return null;
  return (
    <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
      <IconButton aria-label="Previous" onClick={() => scrollTrack(trackRef, -1)} sx={arrowButtonSx}>
        <ArrowBackIosNewIcon sx={{ fontSize: 15 }} />
      </IconButton>
      <IconButton aria-label="Next" onClick={() => scrollTrack(trackRef, 1)} sx={arrowButtonSx}>
        <ArrowForwardIosIcon sx={{ fontSize: 15 }} />
      </IconButton>
    </Box>
  );
};

const arrowButtonSx = {
  width: 38,
  height: 38,
  color: C.hi,
  border: `1px solid ${C.hairlineStrong}`,
  bgcolor: C.surface,
  '&:hover': { bgcolor: C.raised, borderColor: 'rgba(182,242,74,0.35)' },
};

const carouselSx = {
  display: 'flex',
  gap: { xs: 1.25, md: 1.5 },
  overflowX: 'auto',
  scrollSnapType: 'x mandatory',
  pb: 1,
  mx: { xs: -0.5, md: 0 },
  px: { xs: 0.5, md: 0 },
  '&::-webkit-scrollbar': { height: 5 },
  '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 999 },
};

const LandingDropdown = ({ id, label, value, options, onChange, openDropdown, setOpenDropdown, compact }) => {
  const selected = options.find((item) => item.value === value) || options[0];
  const display = compact ? (selected?.mobileLabel || selected?.label) : `${label} ${selected?.label || ''}`;
  const open = openDropdown === id;

  return (
    <Box sx={{ position: 'relative', minWidth: compact ? 0 : 154, flex: compact ? 1 : '0 0 auto' }}>
      <Button
        type="button"
        onClick={() => setOpenDropdown(open ? null : id)}
        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 17 }} />}
        sx={{
          justifyContent: 'space-between',
          width: '100%',
          minHeight: 42,
          px: compact ? 1 : 1.3,
          borderRadius: 2,
          color: C.mid,
          bgcolor: C.surface,
          border: `1px solid ${open ? 'rgba(182,242,74,0.45)' : C.hairlineStrong}`,
          fontFamily: fonts.mono,
          fontSize: 11,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          overflow: 'hidden',
          '& .MuiButton-endIcon': { ml: 0.5 },
          '&:hover': { bgcolor: C.raised, borderColor: 'rgba(182,242,74,0.35)' },
        }}
      >
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {display}
        </Box>
      </Button>
      {open && (
        <Box
          sx={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: 0,
            minWidth: compact ? '100%' : 220,
            maxHeight: 300,
            overflowY: 'auto',
            zIndex: 45,
            p: 0.5,
            bgcolor: C.muted,
            border: `1px solid ${C.hairlineStrong}`,
            borderRadius: 2,
            boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
          }}
        >
          {options.map((option) => (
            <Box
              key={option.value}
              component="button"
              type="button"
              onClick={() => {
                onChange(option.value);
                setOpenDropdown(null);
              }}
              sx={{
                width: '100%',
                display: 'block',
                textAlign: 'left',
                border: 0,
                borderRadius: 1.3,
                px: 1.1,
                py: 1,
                cursor: 'pointer',
                color: option.value === value ? C.lime : C.mid,
                bgcolor: option.value === value ? 'rgba(182,242,74,0.08)' : 'transparent',
                fontFamily: fonts.body,
                fontWeight: option.value === value ? 700 : 500,
                fontSize: 13,
                '&:hover': { bgcolor: C.raised },
              }}
            >
              {option.label}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const TopBar = ({ navOpen, setNavOpen, isMobile, hideExplore = false }) => {
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);

  const routeEntity = (item) => {
    if (item.type === 'player') {
      navigate(playerProfileUrl(item));
    } else if (item.type === 'team') {
      navigate(`/team?team=${encodeURIComponent(item.name)}&autoload=true`);
    } else if (item.type === 'venue') {
      navigate(`/venue?venue=${encodeURIComponent(item.name)}&autoload=true`);
    }
    setSearchOpen(false);
  };

  const searchField = (
    <Box
      sx={{
        width: '100%',
        maxWidth: { xs: 'none', md: 420 },
        ml: { md: 'auto' },
        position: 'relative',
        zIndex: 50,
      }}
    >
      <SearchBar
        onSelect={routeEntity}
        onFallback={(term) => {
          navigate(`/query?nl=${encodeURIComponent(term)}`);
          setSearchOpen(false);
        }}
        placeholder="Search players, teams, venues"
        variant="dark"
      />
    </Box>
  );

  return (
    <Box sx={{ mb: { xs: 3, md: 4.2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4 }}>
        <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', gap: 1, textDecoration: 'none' }}>
          <Box component="img" src="/cricket-icon.svg" alt="" sx={{ width: 32, height: 32 }} />
          <Typography sx={{ color: C.hi, fontFamily: fonts.display, fontWeight: 700, fontSize: { xs: 18, md: 20 } }}>
            Hindsight
          </Typography>
        </Box>
        {!isMobile && searchField}
        {isMobile && (
          <IconButton aria-label="Search" onClick={() => setSearchOpen((prev) => !prev)} sx={topIconSx}>
            <SearchIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}
        {/* Below md the app's bottom nav ("More") is the menu, so the Explore button would
            be a second one. On desktop Home has no tab strip, so it stays. */}
        {!hideExplore && (
        <Button
          type="button"
          onClick={() => setNavOpen(!navOpen)}
          startIcon={<MenuIcon />}
          sx={{
            ml: isMobile ? 0 : 0.5,
            minWidth: isMobile ? 42 : 116,
            width: isMobile ? 42 : 'auto',
            height: 42,
            px: isMobile ? 0 : 1.5,
            borderRadius: 2,
            color: C.bg,
            bgcolor: C.lime,
            fontFamily: fonts.mono,
            fontWeight: 700,
            fontSize: 11,
            letterSpacing: '0.12em',
            '& .MuiButton-startIcon': { mr: isMobile ? 0 : 0.9 },
            '&:hover': { bgcolor: C.limeHover },
          }}
        >
          {isMobile ? '' : 'Explore'}
        </Button>
        )}
      </Box>
      {isMobile && searchOpen && <Box sx={{ mt: 1.3 }}>{searchField}</Box>}
    </Box>
  );
};

const topIconSx = {
  ml: 'auto',
  width: 42,
  height: 42,
  color: C.hi,
  bgcolor: C.surface,
  border: `1px solid ${C.hairlineStrong}`,
  borderRadius: 2,
  '&:hover': { bgcolor: C.raised },
};

// Fixture start in the viewer's own time zone. The API's `date`/`time` are UTC, so without
// `startUtc` (an older cached response) the time is labelled UTC rather than guessed.
const localStart = (match) => {
  if (!match.startUtc) {
    return { date: match.date, label: match.time ? `${match.time} UTC` : null };
  }
  const start = new Date(match.startUtc);
  const date = start.toLocaleDateString('en-CA');
  let time = start.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' });
  // en-US browsers in India name the zone "GMT+5:30"; most of our readers know it as IST.
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (zone === 'Asia/Kolkata' || zone === 'Asia/Calcutta') time = time.replace(/GMT\+5:30$/, 'IST');
  const today = new Date().toLocaleDateString('en-CA');
  const tomorrow = new Date(Date.now() + 864e5).toLocaleDateString('en-CA');
  const day = date === today ? '' : date === tomorrow ? 'Tomorrow ' : `${start.toLocaleDateString(undefined, { weekday: 'short' })} `;
  return { date, label: `${day}${time}` };
};

// The sides and leagues whose matches lead the row, so a big game is never pushed off it by a busy day elsewhere.
const BIG_TEAMS = new Set(['India', 'Australia', 'England', 'South Africa', 'New Zealand', 'Pakistan', 'Sri Lanka',
  'West Indies', 'Afghanistan', 'Bangladesh']);
const BIG_SERIES = /\b(IPL|INDIAN PREMIER LEAGUE|WORLD CUP|ASIA CUP|CHAMPIONS TROPHY|BIG BASH|BBL|PSL|SA20|CPL|THE HUNDRED|MLC)\b/i;
const isBig = (match) => BIG_TEAMS.has(match.team1) || BIG_TEAMS.has(match.team2) || BIG_SERIES.test(match.series || '');
const AHEAD_MS = 36 * 3600e3; // today's matches, plus anything starting in the next day and a half

// Live first, then by day; within a day the big matches lead, then by start time.
const fixtureOrder = (a, b) => (Number(b.isLive) - Number(a.isLive))
  || localStart(a).date.localeCompare(localStart(b).date)
  || (Number(isBig(b)) - Number(isBig(a)))
  || String(a.startUtc || a.time || '').localeCompare(String(b.startUtc || b.time || ''));

const TodaySection = ({ matches, loading, isMobile }) => {
  const trackRef = useRef(null);
  const today = new Date().toLocaleDateString('en-CA');
  const now = Date.now();
  const soon = (match) => match.isLive || localStart(match).date === today
    || (match.startUtc && new Date(match.startUtc).getTime() - now <= AHEAD_MS);
  const soonMatches = matches.filter(soon).sort(fixtureOrder);
  const displayMatches = soonMatches.length ? soonMatches : [...matches].sort(fixtureOrder).slice(0, 6);
  const hasLive = displayMatches.some((match) => match.isLive);
  const hasToday = displayMatches.some((match) => match.isLive || localStart(match).date === today);

  return (
    <Box component="section" sx={{ mb: { xs: 3.75, md: 5.5 } }}>
      <SectionHeader
        kicker={hasLive ? 'Live now' : '01 / Today'}
        kickerColor={hasLive ? C.red : C.lime}
        title="Today's Matches"
        subtitle={hasToday ? 'Live, today and the next day and a half: T20 and ODI' : 'No match today, showing the next fixtures'}
        action={<CarouselButtons trackRef={trackRef} hide={isMobile || loading || displayMatches.length < 2} />}
      />
      {loading ? (
        <LoadingCard label="Loading fixtures..." />
      ) : (
        <Box ref={trackRef} sx={carouselSx}>
          {displayMatches.map((match) => (
            <TodayMatchCard key={`${match.matchId || match.matchNumber}-${match.date}`} match={match} />
          ))}
          {!displayMatches.length && <EmptyCard label="No live or upcoming fixtures found." />}
        </Box>
      )}
    </Box>
  );
};

const TodayMatchCard = ({ match }) => {
  const live = Boolean(match.isLive);
  const startLabel = localStart(match).label;
  const status = live ? (match.statusText || 'Live') : (startLabel ? `Starts ${startLabel}` : 'Scheduled');
  const team1Color = readableOnDark(getTeamColor(match.team1Abbr || match.team1) || C.low);
  const team2Color = readableOnDark(getTeamColor(match.team2Abbr || match.team2) || C.low);
  return (
    <Box
      sx={{
        scrollSnapAlign: 'start',
        flex: '0 0 auto',
        width: { xs: 290, md: 360 },
        minHeight: 236,
        p: 2,
        borderRadius: 3,
        bgcolor: C.surface,
        background: live ? 'linear-gradient(150deg,#16110f,#101319)' : C.surface,
        border: `1px solid ${live ? 'rgba(229,72,77,0.5)' : C.hairline}`,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.2, mb: 2 }}>
        <Typography sx={chipSx}>{match.format === 'ODI' ? `ODI · ${match.series || ''}` : (match.series || 'T20')}</Typography>
        <Typography sx={{ ...monoSx, color: live ? C.red : C.soft }}>{status}</Typography>
      </Box>
      <TeamFixtureRow color={team1Color} abbr={match.team1Abbr || match.team1} name={match.team1} />
      <Typography sx={{ color: C.soft, fontFamily: fonts.mono, fontSize: 11, lineHeight: 1, textAlign: 'center', my: 0.7 }}>
        VS
      </Typography>
      <TeamFixtureRow color={team2Color} abbr={match.team2Abbr || match.team2} name={match.team2} />
      <Box sx={{ height: '1px', bgcolor: C.hairline, my: 1.7 }} />
      <Typography sx={{ color: C.soft, fontFamily: fonts.body, fontSize: 13, minHeight: 36 }}>
        {match.venue || 'Venue TBA'}
      </Typography>
      <Button component={Link} to={routePreview(match)} fullWidth sx={limeButtonSx}>
        Match Preview
      </Button>
    </Box>
  );
};

const TeamFixtureRow = ({ color, abbr, name }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
    <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
    <Typography component={Link} to={routeTeam(abbr)} sx={teamLinkSx}>
      {abbr}
    </Typography>
    <Typography sx={{ color: C.soft, fontFamily: fonts.body, fontSize: 13, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {name}
    </Typography>
  </Box>
);

const chipSx = {
  color: C.mid,
  bgcolor: C.muted,
  px: 0.8,
  py: 0.45,
  borderRadius: 1,
  fontFamily: fonts.mono,
  fontSize: 11,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  maxWidth: '62%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const monoSx = {
  fontFamily: fonts.mono,
  fontSize: 11,
  letterSpacing: '0.04em',
  whiteSpace: 'nowrap',
};

const teamLinkSx = {
  color: C.hi,
  fontFamily: fonts.display,
  fontWeight: 700,
  fontSize: 23,
  lineHeight: 1,
  // 32px tap target without changing the type size ("SL" alone is 23px wide).
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 32,
  minWidth: 32,
  textDecoration: 'none',
  '&:hover': { color: C.lime },
};

const limeButtonSx = {
  mt: 1.7,
  height: 42,
  borderRadius: 2,
  bgcolor: C.lime,
  color: C.bg,
  fontFamily: fonts.mono,
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  '&:hover': { bgcolor: C.limeHover },
};


// The Hindsight MCP connector (mcp_server/): lets Claude / ChatGPT query the data directly.
const CONNECTOR_URL = 'https://hindsightcricket.com/mcp';
const CONNECTOR_PROMPTS = [
  "Kohli's strike rate vs pace and spin by year in T20s",
  'Top 10 IPL 2026 strike rates, min 100 balls',
  'Preview South Africa v Australia at Kingsmead in ODIs',
];

const ConnectorSection = () => {
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);
  // hindsightcricket.com/mcp opened in a browser lands here (vercel.json). The sections above load after this one
  // renders and push it down, so scroll again as they arrive, for a few seconds, until the reader scrolls themselves.
  useEffect(() => {
    if (window.location.hash !== '#connector') return undefined;
    let stopped = false;
    const stop = () => { stopped = true; };
    const go = () => { if (!stopped && ref.current) ref.current.scrollIntoView({ block: 'start' }); };
    const timers = [0, 400, 1000, 2000, 3500, 5000].map((ms) => setTimeout(go, ms));
    window.addEventListener('wheel', stop, { passive: true });
    window.addEventListener('touchstart', stop, { passive: true });
    window.addEventListener('keydown', stop);
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener('wheel', stop);
      window.removeEventListener('touchstart', stop);
      window.removeEventListener('keydown', stop);
    };
  }, []);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(CONNECTOR_URL);
      track('connector_copy');
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };
  const step = (label, text) => (
    <Typography sx={{ color: C.mid, fontFamily: fonts.body, fontSize: 14, lineHeight: 1.5 }}>
      <Box component="span" sx={{ color: C.hi, fontWeight: 700 }}>{label}</Box> {text}
    </Typography>
  );
  return (
    <Box component="section" id="connector" ref={ref} sx={{ mb: { xs: 3.75, md: 5.5 }, scrollMarginTop: 72 }}>
      <SectionHeader
        kicker="05 / Connect"
        title="Use Hindsight in Claude or ChatGPT"
        subtitle="Ask questions in plain English and get tables, charts and a link back here."
      />
      <Box sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3, bgcolor: C.surface, border: `1px solid ${C.hairline}`, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, p: 1, pl: 1.5, borderRadius: 2, bgcolor: C.inset, border: `1px solid ${C.hairlineStrong}` }}>
          <Typography sx={{ ...monoSx, color: C.lime, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, minWidth: 0 }}>
            {CONNECTOR_URL}
          </Typography>
          <Button
            onClick={copy}
            startIcon={copied ? <CheckRoundedIcon /> : <ContentCopyRoundedIcon />}
            sx={{ flexShrink: 0, minHeight: 40, px: 1.5, borderRadius: 2, bgcolor: C.lime, color: C.bg, fontFamily: fonts.mono, fontWeight: 700, fontSize: 11, '&:hover': { bgcolor: C.limeHover } }}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </Box>
        {step('Claude:', 'Settings → Connectors → Add custom connector → paste the URL.')}
        {step('ChatGPT:', 'Settings → Apps & Connectors → turn on Developer mode → Create → paste the URL, no authentication.')}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, pt: 0.5 }}>
          {CONNECTOR_PROMPTS.map((prompt) => (
            <Typography key={prompt} sx={{ ...chipSx, maxWidth: '100%', whiteSpace: 'normal', textTransform: 'none', letterSpacing: 0, fontSize: 12 }}>
              “{prompt}”
            </Typography>
          ))}
        </Box>
        <Typography sx={{ color: C.low, fontFamily: fonts.body, fontSize: 12 }}>
          Read-only and free to use; results are aggregated and rate-limited.
        </Typography>
      </Box>
    </Box>
  );
};

const RecentMatchesSection = ({
  data,
  loading,
  activeCompetition,
  setActiveCompetition,
  teamFilter,
  setTeamFilter,
  dateFilter,
  setDateFilter,
  openDropdown,
  setOpenDropdown,
  isMobile,
}) => {
  const trackRef = useRef(null);
  const matches = useMemo(() => flattenRecentMatches(data), [data]);
  const competitionOptions = useMemo(() => {
    const source = data?.filter_options?.competitions || data?.filters || [];
    return source.map((item) => ({
      value: item.key,
      label: item.label,
      mobileLabel: item.label,
      latest_date: item.latest_date,
    }));
  }, [data]);
  const teamOptions = useMemo(() => {
    const source = data?.filter_options?.teams || data?.team_filters || [];
    const seen = new Set();
    return [
      { value: 'all', label: 'All teams', mobileLabel: 'All teams' },
      ...source
        .filter((item) => item.value && !seen.has(item.value) && seen.add(item.value))
        .map((item) => ({ value: item.value, label: item.label || item.value, mobileLabel: item.label || item.value })),
    ];
  }, [data]);
  const selectedCompetition = activeCompetition || competitionOptions[0]?.value || 'all';
  const narrowed = teamFilter !== 'all' || dateFilter !== 'all';

  return (
    <Box component="section" sx={{ mb: { xs: 3.75, md: 5.5 } }}>
      <SectionHeader
        kicker="02 / Browse"
        title="Recent Matches"
        subtitle={`${matches.length} matches - filter by team, competition & recency`}
        action={<CarouselButtons trackRef={trackRef} hide={isMobile || loading || matches.length < 2} />}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.7, flexWrap: isMobile ? 'nowrap' : 'wrap' }}>
        <LandingDropdown
          id="team"
          label="Team"
          value={teamFilter}
          options={teamOptions}
          onChange={setTeamFilter}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          compact={isMobile}
        />
        <LandingDropdown
          id="comp"
          label="Competition"
          value={selectedCompetition}
          options={competitionOptions.length ? competitionOptions : [{ value: 'all', label: 'All' }]}
          onChange={setActiveCompetition}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          compact={isMobile}
        />
        <LandingDropdown
          id="date"
          label="When"
          value={dateFilter}
          options={DATE_OPTIONS}
          onChange={setDateFilter}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          compact={isMobile}
        />
        {narrowed && !isMobile && (
          <Button
            type="button"
            onClick={() => {
              setTeamFilter('all');
              setDateFilter('all');
            }}
            sx={{
              color: C.lime,
              border: `1px solid rgba(182,242,74,0.35)`,
              borderRadius: 2,
              height: 38,
              px: 1.3,
              fontFamily: fonts.mono,
              fontSize: 11,
              textTransform: 'uppercase',
            }}
          >
            Clear filters
          </Button>
        )}
      </Box>
      {loading ? (
        <LoadingCard label="Loading recent matches..." />
      ) : matches.length ? (
        <Box ref={trackRef} sx={carouselSx}>
          {matches.map((match) => <RecentMatchTile key={match.match_id} match={match} />)}
        </Box>
      ) : (
        <EmptyCard
          label="No scorecards match these filters."
          actionLabel="Reset"
          onAction={() => {
            setTeamFilter('all');
            setDateFilter('all');
            setActiveCompetition('all');
          }}
        />
      )}
    </Box>
  );
};

const RecentMatchTile = ({ match }) => {
  const winner = match.winner || '';
  const winnerColor = readableOnDark(getTeamColor(winner) || C.lime);
  const team1Won = winner && [match.team1, match.team1_full].some((team) => String(team || '').toLowerCase() === winner.toLowerCase());
  const team2Won = winner && [match.team2, match.team2_full].some((team) => String(team || '').toLowerCase() === winner.toLowerCase());

  return (
    <Box
      sx={{
        scrollSnapAlign: 'start',
        flex: '0 0 auto',
        width: { xs: 234, md: 252 },
        minHeight: 230,
        p: 1.45,
        borderRadius: 2.5,
        bgcolor: C.surface,
        border: `1px solid ${C.hairline}`,
        borderLeft: `4px solid ${winnerColor}`,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1.4 }}>
        <Typography sx={{ ...chipSx, maxWidth: '58%' }}>{match.competition_abbr || match.competition_display || match.competition}</Typography>
        <Typography sx={{ ...monoSx, color: C.low }}>{toDateLabel(match.date)}</Typography>
      </Box>
      <ScoreRow team={match.team1} score={match.innings1_score || match.team1_score} winner={team1Won} />
      <ScoreRow team={match.team2} score={match.innings2_score || match.team2_score} winner={team2Won} />
      <Box sx={{ height: '1px', bgcolor: C.hairline, my: 1.35 }} />
      <Typography
        component={Link}
        to={`/scorecard/${match.match_id}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          minHeight: 34,
          color: C.mid,
          fontFamily: fonts.body,
          fontSize: 13,
          lineHeight: 1.25,
          fontWeight: 600,
          textDecoration: 'none',
          '&:hover': { color: C.lime },
        }}
      >
        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: winnerColor, flexShrink: 0 }} />
        {match.result_text || (winner ? `${winner} won` : 'Result unavailable')}
      </Typography>
      <Typography sx={{ color: C.low, fontFamily: fonts.body, fontSize: 12, lineHeight: 1.25, minHeight: 32, mt: 0.4 }}>
        {match.venue || 'Venue unavailable'}
      </Typography>
      <Button component={Link} to={`/scorecard/${match.match_id}`} fullWidth sx={limeButtonSx}>
        Scorecard
      </Button>
    </Box>
  );
};

const ScoreRow = ({ team, score, winner }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.25 }}>
    <Typography component={Link} to={routeTeam(team)} sx={{
      color: winner ? C.lime : C.mid,
      fontFamily: fonts.display,
      fontWeight: winner ? 700 : 600,
      fontSize: 16,
      lineHeight: 1,
      display: 'inline-flex',
      alignItems: 'center',
      minHeight: 32,
      textDecoration: 'none',
      '&:hover': { color: C.lime },
    }}>
      {team}
    </Typography>
    <Typography sx={{
      color: winner ? C.lime : C.soft,
      fontFamily: fonts.display,
      fontWeight: winner ? 700 : 600,
      fontSize: 14,
      fontVariantNumeric: 'tabular-nums',
      whiteSpace: 'nowrap',
    }}>
      {score || '-'}
    </Typography>
  </Box>
);

const FeaturedInningsSection = ({ innings, loading, isMobile }) => {
  const trackRef = useRef(null);
  return (
    <Box component="section" sx={{ mb: { xs: 3.75, md: 5.5 } }}>
      <SectionHeader
        kicker="03 / Form"
        title="Recent Standout Innings"
        subtitle="Top hitting from the last 30 days - tap a name for the full profile"
        action={<CarouselButtons trackRef={trackRef} hide={isMobile || loading || innings.length < 2} />}
      />
      {loading ? (
        <LoadingCard label="Loading standout innings..." />
      ) : (
        <Box ref={trackRef} sx={carouselSx}>
          {innings.map((item, index) => (
            <Box
              key={`${item.batter}-${item.date}-${index}`}
              component={Link}
              to={routePlayer(item.batter)}
              sx={{
                scrollSnapAlign: 'start',
                flex: '0 0 auto',
                width: { xs: 280, md: 310 },
                minHeight: 154,
                p: 1.45,
                display: 'flex',
                gap: 1.4,
                alignItems: 'center',
                textDecoration: 'none',
                bgcolor: C.surface,
                border: `1px solid ${C.hairline}`,
                borderRadius: 2.5,
                '&:hover': { bgcolor: C.raised, borderColor: 'rgba(182,242,74,0.25)' },
              }}
            >
              <MiniWagonWheel deliveries={item.deliveries || []} size={isMobile ? 96 : 108} variant="dark" />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ color: C.lime, fontFamily: fonts.display, fontWeight: 700, fontSize: 17, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.batter}
                </Typography>
                <Typography sx={{ color: C.hi, fontFamily: fonts.display, fontWeight: 700, fontSize: 26, lineHeight: 1, fontVariantNumeric: 'tabular-nums', mt: 0.4 }}>
                  {item.runs} <Box component="span" sx={{ color: C.soft, fontSize: 16 }}>({item.balls})</Box>
                </Typography>
                <Typography sx={{ color: C.mid, fontFamily: fonts.mono, fontSize: 11, mt: 0.8 }}>
                  SR {item.strike_rate} - {item.fours}x4 - {item.sixes}x6
                </Typography>
                <Typography sx={{ color: C.soft, fontFamily: fonts.body, fontSize: 12.5, mt: 0.7, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.team} v {item.opponent}
                </Typography>
                <Typography sx={{ color: C.low, fontFamily: fonts.body, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.venue} - {item.date}
                </Typography>
              </Box>
            </Box>
          ))}
          {!innings.length && <EmptyCard label="No standout innings available." />}
        </Box>
      )}
    </Box>
  );
};

const EloSection = ({ isMobile, openDropdown, setOpenDropdown }) => {
  // Pinned, not the raw selection: ELO is one rating stream per format, so "All formats" has
  // no meaning here and /teams/elo-rankings cannot resolve it. Falls back to men's T20.
  const { pinnedFormatParams: formatParams } = useFormat();
  const [eloComp, setEloComp] = useState('international');
  const [eloRange, setEloRange] = useState('all');
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchRankings = async () => {
      setLoading(true);
      const params = new URLSearchParams();
      if (eloComp === 'international') {
        params.set('include_international', 'true');
        params.set('top_teams', '10');
      } else {
        params.set('include_international', 'false');
        params.set('league', 'Indian Premier League');
      }
      const now = new Date();
      if (eloRange === '24m') {
        const start = new Date(now);
        start.setMonth(start.getMonth() - 24);
        params.set('start_date', dateInput(start));
        params.set('end_date', dateInput(now));
      } else if (eloRange === '12m') {
        const start = new Date(now);
        start.setMonth(start.getMonth() - 12);
        params.set('start_date', dateInput(start));
        params.set('end_date', dateInput(now));
      } else if (eloRange === 'ytd') {
        params.set('start_date', `${now.getFullYear()}-01-01`);
        params.set('end_date', dateInput(now));
      }
      // ELO is a separate stream per format, so the ranking must be scoped to the selection.
      params.set('format', formatParams.format);
      params.set('gender', formatParams.gender);
      try {
        const response = await fetch(
          `${config.API_URL}/teams/elo-rankings?${params.toString()}`,
        );
        const payload = await response.json();
        if (!cancelled) setRankings(payload.rankings || []);
      } catch (error) {
        console.error('Error fetching ELO rankings:', error);
        if (!cancelled) setRankings([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchRankings();
    return () => { cancelled = true; };
  }, [eloComp, eloRange, formatParams.format, formatParams.gender]);

  return (
    <Box component="section" sx={{ mb: { xs: 3.75, md: 5.5 } }}>
      <SectionHeader kicker="04 / Rank" kickerColor={C.gold} title="ELO Team Rankings" />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.7, flexWrap: 'wrap' }}>
        <LandingDropdown
          id="elo"
          label="Competition"
          value={eloComp}
          options={[
            { value: 'international', label: 'International Teams' },
            { value: 'ipl', label: 'IPL Teams' },
          ]}
          onChange={setEloComp}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          compact={isMobile}
        />
        <LandingDropdown
          id="eloRange"
          label="Range"
          value={eloRange}
          options={ELO_RANGE_OPTIONS}
          onChange={setEloRange}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          compact={isMobile}
        />
        <Typography sx={{ ml: { md: 'auto' }, color: C.low, fontFamily: fonts.mono, fontSize: 11, letterSpacing: '0.08em' }}>
          K-FACTOR 32 - TOP 10
        </Typography>
      </Box>
      {loading ? (
        <LoadingCard label="Loading ELO rankings..." />
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 1.15 }}>
          {rankings.slice(0, 10).map((team) => <EloRow key={team.team_name} team={team} />)}
        </Box>
      )}
    </Box>
  );
};

const EloRow = ({ team }) => {
  const color = getTeamColor(team.team_abbreviation || team.team_name) || C.low;
  const rank = Number(team.rank || 0);
  const medal = rank === 1 ? C.gold : rank === 2 ? '#c0c0c0' : rank === 3 ? '#cd7f32' : 'transparent';
  const winPct = Number(team.win_percentage || 0);
  const winColor = winPct >= 60 ? C.lime : winPct >= 45 ? C.gold : C.red;

  return (
    <Box
      component={Link}
      to={routeTeam(team.team_abbreviation || team.team_name)}
      sx={{
        display: 'grid',
        gridTemplateColumns: '42px minmax(0,1fr) auto',
        alignItems: 'center',
        gap: 1.2,
        p: 1.25,
        minHeight: 70,
        bgcolor: C.surface,
        border: `1px solid ${C.hairline}`,
        borderRadius: 2.2,
        textDecoration: 'none',
        '&:hover': { bgcolor: C.raised, borderColor: 'rgba(182,242,74,0.25)' },
      }}
    >
      <Box sx={{
        width: 34,
        height: 34,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        bgcolor: color,
        color: textOn(color),
        border: rank <= 3 ? `2px solid ${medal}` : '0',
        fontFamily: fonts.display,
        fontWeight: 700,
        fontSize: 15,
      }}>
        {rank}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: C.hi, fontFamily: fonts.display, fontWeight: 700, fontSize: 17, lineHeight: 1 }}>
          {team.team_abbreviation || team.team_name}
        </Typography>
        <Typography sx={{ color: C.soft, fontFamily: fonts.body, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mt: 0.35 }}>
          {team.team_name}
        </Typography>
        <Typography sx={{ color: winColor, fontFamily: fonts.mono, fontSize: 11, mt: 0.45 }}>
          {team.wins || 0}-{team.losses || 0} - {Math.round(winPct)}%
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <Typography sx={{ color: C.lime, fontFamily: fonts.display, fontWeight: 700, fontSize: 19, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
          {Math.round(Number(team.current_elo || 0))}
        </Typography>
        <Typography sx={{ color: C.low, fontFamily: fonts.mono, fontSize: 11, letterSpacing: '0.1em' }}>
          ELO
        </Typography>
      </Box>
    </Box>
  );
};

const LEAGUE_COUNTS_PREVIEW = 10;

const LeagueCountsSection = ({ stats, showLeagueCounts = true }) => {
  // A health check, not a headline: the biggest ten competitions, the other ~50 on request.
  const [showAll, setShowAll] = useState(false);
  if (!showLeagueCounts) return null;
  const items = Object.values(stats || {}).sort((a, b) => (b.match_count || 0) - (a.match_count || 0));
  if (!items.length) return null;
  const visible = showAll ? items : items.slice(0, LEAGUE_COUNTS_PREVIEW);

  return (
    <Box component="section" sx={{ mb: { xs: 3.75, md: 5.5 } }}>
      <Kicker>06 / Pipeline</Kicker>
      <Typography sx={{ ...sectionTitleSx, fontSize: { xs: 17, md: 20 } }}>League Match Counts</Typography>
      <Typography sx={{ color: C.soft, mt: 0.6, mb: 1.8, fontFamily: fonts.body, fontSize: 13 }}>
        Ingested match totals per competition - data-pipeline health check
      </Typography>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', md: 'repeat(4, minmax(0,1fr))', lg: 'repeat(5, minmax(0,1fr))' },
        gap: 1,
      }}>
        {visible.map((item) => (
          <Box key={item.competition_key || item.competition} sx={{ p: 1.35, bgcolor: C.muted, border: `1px solid ${C.hairline}`, borderRadius: 2 }}>
            <Typography sx={{ color: C.mid, fontFamily: fonts.display, fontWeight: 700, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item.competition_display || item.competition}
            </Typography>
            <Typography sx={{ color: C.hi, fontFamily: fonts.display, fontWeight: 700, fontSize: 24, fontVariantNumeric: 'tabular-nums', lineHeight: 1.05, mt: 0.7 }}>
              {(item.match_count || 0).toLocaleString()}
            </Typography>
            <Typography sx={{ color: C.low, fontFamily: fonts.mono, fontSize: 11, mt: 0.5 }}>
              latest {toDateLabel(item.latest_date)}
            </Typography>
          </Box>
        ))}
      </Box>
      {items.length > LEAGUE_COUNTS_PREVIEW && (
        <Box
          component="button"
          type="button"
          onClick={() => setShowAll((prev) => !prev)}
          sx={{ mt: 1.25, minHeight: 40, px: 1.5, bgcolor: 'transparent', color: C.lime, border: `1px solid ${C.hairlineStrong}`, borderRadius: 2, fontFamily: fonts.mono, fontSize: 12, cursor: 'pointer' }}
        >
          {showAll ? 'Show fewer' : `Show all ${items.length}`}
        </Box>
      )}
    </Box>
  );
};

const CreditsSection = () => {
  const year = new Date().getFullYear();
  return (
    <Box component="section" sx={{ borderTop: `1px solid ${C.hairline}`, pt: { xs: 2.6, md: 3.3 }, pb: 2 }}>
      <Kicker>06 / Thanks</Kicker>
      <Typography sx={sectionTitleSx}>Credits & Acknowledgements</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.3, mt: 1.8 }}>
        <CreditCard title="Data sources">
          <CreditLink href="https://cricsheet.org/">Cricsheet.org</CreditLink>
          <CreditLink href="https://cricmetric.com/">Cricmetric</CreditLink>
        </CreditCard>
        <CreditCard title="Inspiration">
          {['@prasannalara', '@cricketingview', '@IndianMourinho', '@hganjoo_153', '@randomcricstat', '@kaustats', '@cricviz', '@ajarrodkimber'].map((handle) => (
            <CreditChip key={handle} href={`https://twitter.com/${handle.slice(1)}`}>{handle}</CreditChip>
          ))}
        </CreditCard>
        <CreditCard title="Development">
          <Typography sx={{ color: C.mid, fontFamily: fonts.body, fontSize: 13, mb: 1.2 }}>
            Claude and ChatGPT for Vibe Coding my way through this project.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <CreditChip href="https://github.com/adityabalaji97/cricket-data-thing" icon={<GitHubIcon sx={{ fontSize: 14 }} />}>GitHub</CreditChip>
            <CreditChip href="https://twitter.com/maybe_eybe" icon={<XIcon sx={{ fontSize: 13 }} />}>@maybe_eybe</CreditChip>
          </Box>
          <Typography sx={{ color: C.low, fontFamily: fonts.mono, fontSize: 11, mt: 1.4 }}>
            Hindsight (c) {year}
          </Typography>
        </CreditCard>
      </Box>
    </Box>
  );
};

const CreditCard = ({ title, children }) => (
  <Box sx={{ p: 1.6, bgcolor: C.surface, border: `1px solid ${C.hairline}`, borderRadius: 2.4 }}>
    <Typography sx={{ color: C.hi, fontFamily: fonts.display, fontWeight: 700, fontSize: 17, mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const CreditLink = ({ href, children }) => (
  <Typography component="a" href={href} target="_blank" rel="noopener noreferrer" sx={{
    display: 'block',
    color: C.lime,
    fontFamily: fonts.body,
    fontWeight: 600,
    fontSize: 14,
    textDecoration: 'none',
    mb: 0.8,
    '&:hover': { color: C.limeHover },
  }}>
    {children}
  </Typography>
);

const CreditChip = ({ href, icon, children }) => (
  <Box component="a" href={href} target="_blank" rel="noopener noreferrer" sx={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    mr: 0.6,
    mb: 0.7,
    px: 0.75,
    py: 0.45,
    color: C.mid,
    bgcolor: C.muted,
    border: `1px solid ${C.hairline}`,
    borderRadius: 1,
    fontFamily: fonts.mono,
    fontSize: 11,
    textDecoration: 'none',
    '&:hover': { color: C.lime, borderColor: 'rgba(182,242,74,0.35)' },
  }}>
    {icon}
    {children}
  </Box>
);

const LoadingCard = ({ label }) => (
  <Box sx={{ minHeight: 130, display: 'grid', placeItems: 'center', bgcolor: C.surface, border: `1px solid ${C.hairline}`, borderRadius: 2.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, color: C.soft }}>
      <CircularProgress size={18} sx={{ color: C.lime }} />
      <Typography sx={{ fontFamily: fonts.body, fontSize: 14 }}>{label}</Typography>
    </Box>
  </Box>
);

const EmptyCard = ({ label, actionLabel, onAction }) => (
  <Box sx={{ minHeight: 130, width: '100%', display: 'grid', placeItems: 'center', bgcolor: C.surface, border: `1px solid ${C.hairline}`, borderRadius: 2.5, p: 2 }}>
    <Box sx={{ textAlign: 'center' }}>
      <Typography sx={{ color: C.soft, fontFamily: fonts.body, fontSize: 14 }}>{label}</Typography>
      {actionLabel && (
        <Button type="button" onClick={onAction} sx={{ mt: 1, color: C.lime, fontFamily: fonts.display, fontWeight: 700 }}>
          {actionLabel} <ChevronRightIcon sx={{ fontSize: 16 }} />
        </Button>
      )}
    </Box>
  </Box>
);

// Daily games (growth plan G2): the day's puzzles, with this browser's played state.
const DAILY_LAUNCH = Date.UTC(2026, 8, 26);
const istToday = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
const dailyNumber = () => Math.floor((Date.parse(`${istToday()}T00:00:00Z`) - DAILY_LAUNCH) / 86400000) + 1;
const playedToday = (game) => {
  try {
    return JSON.parse(localStorage.getItem(`hindsight.daily.${game}.stats`) || '{}').lastPlayed === istToday();
  } catch {
    return false;
  }
};

export const TodaysGamesSection = () => {
  const number = dailyNumber();
  const games = [
    { key: 'call_it', to: '/games/call-it', title: 'Call It', blurb: 'Five real chases, frozen mid-game. Did they win it?' },
    { key: 'higher_lower', to: '/games/higher-lower', title: 'Higher or Lower', blurb: 'Tap the player-season with the higher Impact. Ten pairs a day.' },
    { key: 'player_journeys', to: '/games/player-journeys', title: 'Player Journeys', blurb: 'Name the IPL player from their franchise path.' },
    { key: 'guess_innings', to: '/games/guess-innings', title: 'Guess the Innings', blurb: 'Whose innings was it? A score line and a wagon wheel.' },
  ];
  return (
    <Box component="section" sx={{ mb: { xs: 3.75, md: 5.5 } }}>
      <Kicker>Daily · #{number}</Kicker>
      <Typography sx={{ ...sectionTitleSx, fontSize: { xs: 20, md: 24 }, mb: 1.5 }}>Today&apos;s games</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 1.25 }}>
        {games.map((game) => {
          const done = playedToday(game.key);
          return (
            <Box
              key={game.key}
              component={Link}
              to={game.to}
              sx={{ p: 2, borderRadius: 2.5, bgcolor: C.surface, border: `1px solid ${done ? C.hairline : 'rgba(182,242,74,0.35)'}`, textDecoration: 'none', display: 'block', '&:hover': { bgcolor: C.raised } }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Typography sx={{ color: C.hi, fontFamily: fonts.display, fontWeight: 700, fontSize: 20 }}>{game.title}</Typography>
                <Typography sx={{ color: done ? C.soft : C.lime, fontFamily: fonts.mono, fontSize: 12 }}>{done ? 'Played ✓' : 'Play →'}</Typography>
              </Box>
              <Typography sx={{ color: C.soft, fontSize: 13.5, mt: 0.5 }}>{game.blurb}</Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// "Latest notes": the three newest published notes (routers/notes.py). Hidden until there are any.
const NOTE_KIND = { recap: 'Match recap', preview: 'Preview', analysis: 'Analysis', article: 'Article' };

const LatestNotesSection = () => {
  const [notes, setNotes] = useState([]);
  useEffect(() => {
    let cancelled = false;
    fetch(`${config.API_URL}/notes?limit=3`)
      .then((r) => (r.ok ? r.json() : { notes: [] }))
      .then((d) => { if (!cancelled) setNotes(d.notes || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  if (!notes.length) return null;
  return (
    <Box component="section" sx={{ mb: { xs: 3.75, md: 5.5 } }}>
      <SectionHeader
        kicker="Notes"
        title="Latest notes"
        action={<Typography component={Link} to="/notes" sx={{ color: C.lime, fontFamily: fonts.mono, fontSize: 12, textDecoration: 'none', whiteSpace: 'nowrap' }}>All notes →</Typography>}
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.25 }}>
        {notes.map((note) => (
          <Box key={note.id} component={Link} to={`/notes/${note.slug}`}
            sx={{ display: 'flex', gap: 1.5, p: 1.75, borderRadius: 2.5, bgcolor: C.surface, border: `1px solid ${C.hairline}`, textDecoration: 'none', minWidth: 0, '&:hover': { bgcolor: C.raised } }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ color: C.lime, fontFamily: fonts.mono, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', mb: 0.5 }}>
                {NOTE_KIND[note.kind] || 'Note'}
              </Typography>
              <Typography sx={{ color: C.hi, fontFamily: fonts.display, fontWeight: 700, fontSize: 17, lineHeight: 1.2,
                display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {note.title}
              </Typography>
              <Typography sx={{ color: C.soft, fontSize: 12.5, mt: 0.75 }}>
                {note.author?.name}{note.author?.is_bot ? ' (AI)' : ''}
              </Typography>
            </Box>
            {note.cover && (
              <Box component="img" alt="" loading="lazy" src={`${siteOrigin()}/img/${note.cover}.png?size=square`}
                sx={{ width: 64, height: 64, flexShrink: 0, borderRadius: 1.5, objectFit: 'cover', bgcolor: C.inset, alignSelf: 'center' }} />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const LandingPage = ({ showLeagueCounts = true }) => {
  // Match the app-wide breakpoint from App.js rather than a page-specific pixel value;
  // three different mobile thresholds across the hero pages made behaviour inconsistent.
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isCompactNav = useMediaQuery(theme.breakpoints.down('md'));
  // Pinned: /landing/featured-innings 500s on format=ALL (verified), because it resolves a
  // FormatSpec to pick strike-rate bands. The landing showcase is single-format by nature.
  const { pinnedFormatParams: formatParams } = useFormat();
  const [navOpen, setNavOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [fixtures, setFixtures] = useState([]);
  const [fixturesLoading, setFixturesLoading] = useState(true);
  const [recentData, setRecentData] = useState(null);
  const [recentLoading, setRecentLoading] = useState(true);
  const [activeCompetition, setActiveCompetition] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [innings, setInnings] = useState([]);
  const [inningsLoading, setInningsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const loadFixtures = async () => {
      setFixturesLoading(true);
      const nextMatches = await fetchUpcomingMatches(20);
      if (!cancelled) {
        setFixtures(nextMatches);
        setFixturesLoading(false);
      }
    };
    loadFixtures();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadRecent = async () => {
      setRecentLoading(true);
      const params = new URLSearchParams({
        ...formatParams,
        competition: activeCompetition || 'all',
        limit: '18',
        offset: '0',
        per_group: '4',
        // One date-sorted list across every competition and format. Without this the carousel
        // renders group-major and shows a run of ODIs, then a run of Hundred matches, instead
        // of the most recent cricket.
        flat: 'true',
        window: dateFilter,
      });
      if (teamFilter !== 'all') params.set('team', teamFilter);
      try {
        const response = await fetch(`${config.API_URL}/recent-matches/discover?${params.toString()}`);
        const payload = await response.json();
        if (cancelled) return;
        setRecentData(payload);
      } catch (error) {
        console.error('Error fetching recent matches:', error);
        if (!cancelled) setRecentData(null);
      } finally {
        if (!cancelled) setRecentLoading(false);
      }
    };
    loadRecent();
    return () => { cancelled = true; };
  }, [activeCompetition, teamFilter, dateFilter]);

  useEffect(() => {
    let cancelled = false;
    const loadInnings = async () => {
      setInningsLoading(true);
      try {
        const response = await fetch(`${config.API_URL}/landing/featured-innings`);
        const payload = await response.json();
        if (!cancelled) setInnings(Array.isArray(payload) ? payload : []);
      } catch (error) {
        console.error('Error fetching featured innings:', error);
        if (!cancelled) setInnings([]);
      } finally {
        if (!cancelled) setInningsLoading(false);
      }
    };
    loadInnings();
    return () => { cancelled = true; };
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: C.bg,
        color: C.hi,
        fontFamily: fonts.body,
        px: { xs: '14px', md: '34px' },
        pt: { xs: '16px', md: '26px' },
        pb: { xs: '56px', md: '72px' },
        '@keyframes livepulse': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.55 },
        },
      }}
    >
      {openDropdown && <Box onClick={() => setOpenDropdown(null)} sx={{ position: 'fixed', inset: 0, zIndex: 35 }} />}
      <Box sx={{ maxWidth: 1220, mx: 'auto' }}>
        <TopBar navOpen={navOpen} setNavOpen={setNavOpen} isMobile={isMobile} hideExplore={isCompactNav} />
        <TodaySection matches={fixtures} loading={fixturesLoading} isMobile={isMobile} />
        <TodaysGamesSection />
        <LatestNotesSection />
        <RecentMatchesSection
          data={recentData}
          loading={recentLoading}
          activeCompetition={activeCompetition}
          setActiveCompetition={setActiveCompetition}
          teamFilter={teamFilter}
          setTeamFilter={setTeamFilter}
          dateFilter={dateFilter}
          setDateFilter={setDateFilter}
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
          isMobile={isMobile}
        />
        <FeaturedInningsSection innings={innings} loading={inningsLoading} isMobile={isMobile} />
        <EloSection isMobile={isMobile} openDropdown={openDropdown} setOpenDropdown={setOpenDropdown} />
        <ConnectorSection />
        <LeagueCountsSection stats={recentData?.competition_stats} showLeagueCounts={showLeagueCounts} />
        <CreditsSection />
      </Box>
      <ExploreMenu open={navOpen} onClose={() => setNavOpen(false)} />
    </Box>
  );
};

export default LandingPage;
